import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Search, Filter, Phone, Mail, ChevronRight, Download } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { leads } from '@/lib/api';
import { formatDateTime, formatPhone, statusLabels, statusColors, cn } from '@/lib/utils';
import Pagination from '@/components/Pagination';
import { CreateLeadModal } from '@/components/CreateLeadModal';

const statusFilters = [
  { value: '', label: 'Allt' },
  { value: 'nýr', label: 'Nýir' },
  { value: 'haft_samband', label: 'Haft samband' },
  { value: 'bókað', label: 'Bókaðir' },
  { value: 'lokað', label: 'Lokaðir' },
  { value: 'glatað', label: 'Glataðir' },
];

const LEADS_PER_PAGE = 20;

/**
 * Convert leads array to CSV string with Icelandic headers.
 * Includes UTF-8 BOM for Excel compatibility with Icelandic characters.
 */
function leadsToCSV(leads: import('@/lib/api').Lead[]): string {
  // UTF-8 BOM for Excel to recognize encoding
  const BOM = '\uFEFF';

  // CSV headers in Icelandic
  const headers = ['Nafn', 'Netfang', 'Sími', 'Staða', 'Einkunn', 'Heimilisfang', 'Búið til'];

  // Escape CSV field (handle commas, quotes, newlines)
  const escapeField = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    // If contains comma, quote, or newline, wrap in quotes and escape internal quotes
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  // Format date for CSV
  const formatDateForCSV = (date: string): string => {
    try {
      return new Intl.DateTimeFormat('is-IS', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(date));
    } catch {
      return date;
    }
  };

  // Status labels mapping
  const csvStatusLabels: Record<string, string> = {
    nýr: 'Nýr',
    haft_samband: 'Haft samband',
    bókað: 'Bókað',
    lokað: 'Lokað',
    glatað: 'Glatað',
  };

  // Build CSV rows
  const rows = leads.map(lead => [
    escapeField(lead.caller_name || 'Nafnlaus'),
    escapeField(lead.caller_email),
    escapeField(lead.caller_phone),
    escapeField(csvStatusLabels[lead.status] || lead.status),
    escapeField(lead.lead_score),
    escapeField(lead.property_address),
    escapeField(formatDateForCSV(lead.created_at)),
  ].join(','));

  return BOM + [headers.join(','), ...rows].join('\n');
}

/**
 * Trigger browser download of CSV file.
 */
function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function Leads() {
  const { token } = useAuthStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [offset, setOffset] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['leads', statusFilter, offset],
    queryFn: () => leads.list(token!, {
      status: statusFilter || undefined,
      limit: LEADS_PER_PAGE,
      offset
    }),
    enabled: !!token,
  });

  // Reset to first page when filter changes
  const handleStatusFilterChange = (newStatus: string) => {
    setStatusFilter(newStatus);
    setOffset(0);
  };

  const filteredLeads = data?.data.filter((lead) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      lead.caller_name?.toLowerCase().includes(searchLower) ||
      lead.caller_phone.includes(search) ||
      lead.caller_email?.toLowerCase().includes(searchLower) ||
      lead.property_address?.toLowerCase().includes(searchLower)
    );
  });

  // Export currently filtered leads to CSV
  const handleExportCSV = () => {
    if (!filteredLeads || filteredLeads.length === 0) return;

    const csvContent = leadsToCSV(filteredLeads);
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    const filename = `leads-${today}.csv`;
    downloadCSV(csvContent, filename);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
          <p className="text-gray-600 mt-1">
            {data?.pagination.total || 0} leads samtals
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => setIsCreateModalOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Nýr lead
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Leita að nafni, síma, netfangi..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>

          {/* Status filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="input pl-10 pr-8 appearance-none"
            >
              {statusFilters.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </select>
          </div>

          {/* Export button */}
          <button
            onClick={handleExportCSV}
            disabled={!filteredLeads || filteredLeads.length === 0}
            className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            title="Flytja út sem CSV"
          >
            <Download className="w-4 h-4 mr-2" />
            Útflutningur
          </button>
        </div>
      </div>

      {/* List */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-prima-600" />
          </div>
        ) : filteredLeads?.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">Engir leads fundust</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredLeads?.map((lead) => (
              <Link
                key={lead.id}
                to={`/leads/${lead.id}`}
                className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 transition-colors"
              >
                {/* Score indicator */}
                <div
                  className={cn(
                    'w-10 h-10 rounded-full flex items-center justify-center text-white font-medium text-sm',
                    lead.lead_score >= 80 ? 'bg-green-500' :
                    lead.lead_score >= 50 ? 'bg-yellow-500' : 'bg-gray-400'
                  )}
                >
                  {lead.lead_score}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-gray-900 truncate">
                      {lead.caller_name || 'Nafnlaus'}
                    </p>
                    <span className={statusColors[lead.status] || 'badge-gray'}>
                      {statusLabels[lead.status] || lead.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Phone className="w-3.5 h-3.5" />
                      {formatPhone(lead.caller_phone)}
                    </span>
                    {lead.caller_email && (
                      <span className="flex items-center gap-1 hidden sm:flex">
                        <Mail className="w-3.5 h-3.5" />
                        {lead.caller_email}
                      </span>
                    )}
                  </div>
                </div>

                {/* Property & Agent */}
                <div className="hidden md:block text-right">
                  <p className="text-sm text-gray-900 truncate max-w-[200px]">
                    {lead.property_address || 'Engin eign'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {lead.agent_name || 'Enginn ráðgjafi'}
                  </p>
                </div>

                {/* Date */}
                <div className="hidden sm:block text-right text-sm text-gray-500 w-32">
                  {formatDateTime(lead.created_at)}
                </div>

                <ChevronRight className="w-5 h-5 text-gray-400" />
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {data && data.pagination.total > LEADS_PER_PAGE && (
          <div className="border-t border-gray-200 px-6 py-4">
            <Pagination
              total={data.pagination.total}
              limit={data.pagination.limit}
              offset={data.pagination.offset}
              onPageChange={setOffset}
            />
          </div>
        )}
      </div>

      {/* Create Lead Modal */}
      <CreateLeadModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
      />
    </div>
  );
}
